
export default function PainPoints() {
  const painPoints = [
    {
      icon: 'ri-search-line',
      pain: 'Invisible to Local Customers',
      description: 'Your competitors show up first on Google while qualified customers can\'t find you, even when they\'re searching in your service area.',
      solution: 'Strategic local visibility optimization that puts you in front of customers actively searching for HVAC services right now.',
      color: 'teal'
    },
    {
      icon: 'ri-global-line',
      pain: 'Outdated or No Online Presence',
      description: 'Your website looks like it\'s from 2010, loads slowly, or doesn\'t exist. Mobile users bounce immediately, and you lose credibility before the first call.',
      solution: 'Modern digital presence that builds instant trust, showcases your expertise, and converts visitors into booked appointments 24/7.',
      color: 'teal'
    },
    {
      icon: 'ri-phone-line',
      pain: 'Leads That Never Convert',
      description: 'Your phone rings, but too many calls are tire-kickers, price shoppers, or people outside your service area wasting your time.',
      solution: 'Targeted attraction strategies that pre-qualify prospects, so the leads you get are ready to buy and match your ideal customer profile.',
      color: 'teal'
    },
    {
      icon: 'ri-calendar-line',
      pain: 'Feast or Famine Schedule',
      description: 'Some weeks you\'re turning away work, other weeks your techs are sitting idle. The unpredictability makes planning and growth nearly impossible.',
      solution: 'Predictable lead generation systems that create consistent demand year-round, allowing you to scale your team with confidence.',
      color: 'teal'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      pain: 'Competing on Price Alone',
      description: 'You\'re stuck in price wars with competitors, forced to lower margins just to win jobs, making it hard to invest in quality or growth.',
      solution: 'Value-based positioning that differentiates your service, allowing you to command premium pricing while customers gladly choose you.',
      color: 'teal'
    },
    {
      icon: 'ri-team-line',
      pain: 'Can\'t Scale Beyond Yourself',
      description: 'You\'re the bottleneck - every decision, every customer interaction requires you. Growth means working more hours, not building a real business.',
      solution: 'Proven systems and processes that allow your team to deliver excellence without you, freeing you to work on the business, not just in it.',
      color: 'teal'
    },
    {
      icon: 'ri-star-line',
      pain: 'Inconsistent or Missing Reviews',
      description: 'Potential customers see few reviews or old feedback, making them question your credibility and choose competitors with stronger social proof.',
      solution: 'Systematic reputation building that turns satisfied customers into vocal advocates, creating a steady stream of authentic 5-star reviews.',
      color: 'teal'
    },
    {
      icon: 'ri-shield-cross-line',
      pain: 'Struggling to Attract Quality Technicians',
      description: 'You can\'t find skilled techs who share your values, and high turnover is costing you money, reputation, and sleep. Training new hires feels like starting over every few months.',
      solution: 'Employer branding strategies that position your company as the place top talent wants to work, creating a culture that attracts and retains skilled professionals.',
      color: 'teal'
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="solutions" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <article className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            What Challenges Are Holding Back<br />
            <strong className="text-teal-700">Your HVAC Business?</strong>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            These are the obstacles keeping you from the growth you deserve - and how we help you overcome them.
          </p>
        </article>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {painPoints.map((point, index) => (
            <article
              key={index}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className={`${point.icon} text-3xl text-teal-600`}></i>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {point.pain}
                    </h3>
                    <p className="text-base text-gray-700 leading-relaxed">
                      <strong className="text-red-600">The Problem:</strong> {point.description}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-l-4 border-teal-600">
                    <p className="text-base text-gray-800 leading-relaxed">
                      <strong className="text-teal-600">Our Solution:</strong> {point.solution}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">
            Ready to Solve These Challenges?
          </h3>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Let's discuss your specific situation and create a custom roadmap to overcome these obstacles and achieve sustainable growth.
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="px-8 py-4 bg-white text-teal-600 font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-lg"
          >
            Schedule Your Free Strategy Call
          </button>
        </div>
      </div>
    </section>
  );
}
